import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardActionArea from '@mui/material/CardActionArea'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AddIcon from '@mui/icons-material/Add'
import WorkspacesIcon from '@mui/icons-material/Workspaces'
import ArchiveIcon from '@mui/icons-material/Archive'
import UnarchiveIcon from '@mui/icons-material/Unarchive'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import { getOrg, listRds, createRd, archiveRd, unarchiveRd, type Org, type ResearchDomain } from '../services/authApi'

export function OrgDetailPage() {
  const { orgId } = useParams<{ orgId: string }>()
  const navigate = useNavigate()
  const [org, setOrg] = useState<Org | null>(null)
  const [rds, setRds] = useState<ResearchDomain[]>([])
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [createOpen, setCreateOpen] = useState(false)
  const [showArchived, setShowArchived] = useState(false)

  const load = useCallback(async () => {
    if (!orgId) return
    try {
      setLoading(true)
      const [orgData, rdsData] = await Promise.all([
        getOrg(orgId),
        listRds(orgId, showArchived),
      ])
      setOrg(orgData.org)
      setRds(rdsData)
      setError('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load org')
    } finally {
      setLoading(false)
    }
  }, [orgId, showArchived])

  useEffect(() => { load() }, [load])

  if (!orgId) return null

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <IconButton onClick={() => navigate('/orgs')} size="small">
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h3">{org?.name ?? 'Loading...'}</Typography>
          <Typography variant="body2" color="text.secondary">
            {org?.slug} &middot; {rds.length} research domain{rds.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={<Switch checked={showArchived} onChange={e => setShowArchived(e.target.checked)} size="small" />}
            label={<Typography variant="body2" color="text.secondary">Show archived</Typography>}
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            New Research Domain
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <Typography color="text.secondary">Loading...</Typography>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 2 }}>
          {rds.map(rd => (
            <Card key={rd.id} sx={{ opacity: rd.isArchived ? 0.6 : 1 }}>
              <CardActionArea onClick={() => navigate(`/rds/${rd.id}`)} disabled={!!rd.isArchived}>
                <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box sx={{
                    width: 40, height: 40, borderRadius: '10px',
                    bgcolor: rd.isArchived ? 'grey.400' : 'secondary.main',
                    color: rd.isArchived ? 'grey.100' : 'secondary.contrastText',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <WorkspacesIcon fontSize="small" />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="h5">{rd.name}</Typography>
                      {rd.isArchived ? <Chip label="Archived" size="small" color="default" /> : null}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {rd.slug}
                    </Typography>
                    {rd.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {rd.description}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label={`${rd.memberCount} member${rd.memberCount !== 1 ? 's' : ''}`} size="small" variant="outlined" />
                    </Box>
                  </Box>
                </CardContent>
              </CardActionArea>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, pb: 1 }}>
                {rd.isArchived ? (
                  <Button size="small" startIcon={<UnarchiveIcon />} onClick={async () => {
                    try { await unarchiveRd(rd.id); load() } catch (e) { setError(e instanceof Error ? e.message : 'Failed') }
                  }}>
                    Unarchive
                  </Button>
                ) : (
                  <Button size="small" color="warning" startIcon={<ArchiveIcon />} onClick={async () => {
                    if (!confirm(`Archive "${rd.name}"? It will be hidden from listings and all access will be suspended.`)) return
                    try { await archiveRd(rd.id); load() } catch (e) { setError(e instanceof Error ? e.message : 'Failed') }
                  }}>
                    Archive
                  </Button>
                )}
              </Box>
            </Card>
          ))}
          {rds.length === 0 && !loading && (
            <Typography color="text.secondary">
              No research domains yet. Create one to start managing members, roles, and policies.
            </Typography>
          )}
        </Box>
      )}

      <CreateRdDialog
        open={createOpen}
        orgId={orgId}
        onClose={() => setCreateOpen(false)}
        onCreated={() => { setCreateOpen(false); load() }}
      />
    </Box>
  )
}

function CreateRdDialog({ open, orgId, onClose, onCreated }: {
  open: boolean; orgId: string; onClose: () => void; onCreated: () => void
}) {
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [description, setDescription] = useState('')
  const [creatorEmail, setCreatorEmail] = useState('')
  const [creatorUserId, setCreatorUserId] = useState('')
  const [creatorName, setCreatorName] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  function handleNameChange(value: string) {
    setName(value)
    setSlug(value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''))
  }

  async function handleCreate() {
    setError('')
    setLoading(true)
    try {
      await createRd(orgId, {
        name, slug, description,
        creatorEmail,
        creatorUserId: creatorUserId || undefined,
        creatorName: creatorName || undefined,
      })
      setName(''); setSlug(''); setDescription('')
      setCreatorEmail(''); setCreatorUserId(''); setCreatorName('')
      onCreated()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create research domain')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Research Domain</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          fullWidth label="Domain Name" value={name}
          onChange={e => handleNameChange(e.target.value)}
          sx={{ mt: 1, mb: 2 }}
        />
        <TextField
          fullWidth label="Slug" value={slug}
          onChange={e => setSlug(e.target.value)}
          helperText="URL-safe identifier (lowercase, hyphens)"
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth label="Description" value={description}
          onChange={e => setDescription(e.target.value)}
          multiline rows={2}
          sx={{ mb: 2 }}
        />
        <Typography variant="overline" sx={{ mt: 1, mb: 1, display: 'block' }}>Owner</Typography>
        <TextField
          fullWidth label="Owner Email" value={creatorEmail}
          onChange={e => setCreatorEmail(e.target.value)}
          type="email"
          required
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth label="Owner Name" value={creatorName}
          onChange={e => setCreatorName(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth label="Cognito User ID" value={creatorUserId}
          onChange={e => setCreatorUserId(e.target.value)}
          helperText="Optional — Cognito sub. If blank, a new ID will be generated."
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleCreate} disabled={loading || !name || !slug || !creatorEmail}>
          {loading ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
